/**
 * Size finder.
 *
 * Reads its options straight off the rendered size-chart table, so the quiz and
 * the chart can never disagree. Recommends from the dress-size band the chart
 * already publishes, with height used only the way the chart's own footnote uses
 * it -- as a size-up nudge for length.
 */
(function () {
  'use strict';

  var STORE_KEY = 'fs:size-finder';

  /** "22–24\"" -> [22, 24]; "00–1" -> [0, 1]; "40" -> [40, 40]. */
  function parseRange(raw) {
    if (!raw) return null;
    var t = String(raw).replace(/["'″′]/g, '');
    var m = t.match(/(\d+(?:\.\d+)?)\s*[–—-]\s*(\d+(?:\.\d+)?)/);
    if (m) return [parseFloat(m[1]), parseFloat(m[2])];
    var one = t.match(/(\d+(?:\.\d+)?)/);
    return one ? [parseFloat(one[1]), parseFloat(one[1])] : null;
  }

  function track(name, detail) {
    try {
      document.dispatchEvent(
        new CustomEvent('fs:size-finder', { detail: Object.assign({ step: name }, detail || {}), bubbles: true })
      );
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'fs_size_finder_' + name }, detail || {}));
    } catch (e) {}
  }

  function init(root) {
    if (root.dataset.fsReady) return;
    root.dataset.fsReady = '1';

    var dialog = root.closest('dialog') || document;
    var table = dialog.querySelector('[data-fs-panel="chart"] table') || dialog.querySelector('table');
    if (!table) return;

    var rows = Array.prototype.map.call(table.querySelectorAll('tr[data-size]'), function (tr) {
      return {
        size: tr.dataset.size,
        dress: tr.dataset.dress,
        waist: parseRange(tr.dataset.waist),
        hips: parseRange(tr.dataset.hips),
        waistRaw: tr.dataset.waist,
        hipsRaw: tr.dataset.hips,
        tr: tr,
      };
    });
    if (!rows.length) return;

    var uk = (root.dataset.ukSizes || '')
      .split(',')
      .map(function (x) { return x.trim(); })
      .filter(Boolean);
    var hasUk = uk.length === rows.length;

    // --- UK column on the chart -------------------------------------------
    if (hasUk && !table.dataset.fsUk) {
      table.dataset.fsUk = '1';
      var headRow = table.querySelector('thead tr');
      if (headRow && headRow.cells.length > 1) {
        var th = document.createElement('th');
        th.textContent = root.dataset.ukLabel || 'UK dress';
        headRow.insertBefore(th, headRow.cells[2] || null);
        rows.forEach(function (r, i) {
          var td = document.createElement('td');
          td.textContent = uk[i];
          r.tr.insertBefore(td, r.tr.cells[2] || null);
        });
      }
    }

    var headCells = table.querySelectorAll('thead th').length;
    if (headCells >= 6) table.classList.add('fs-sizetbl--wide');

    var state = { mode: 'dress', dressIdx: null, height: null, region: 'us', unit: 'in' };
    try {
      var saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
      if (saved && typeof saved.dressIdx === 'number' && rows[saved.dressIdx]) {
        state.dressIdx = saved.dressIdx;
        state.height = saved.height || null;
        state.region = saved.region === 'uk' && hasUk ? 'uk' : 'us';
      }
    } catch (e) {}

    var $ = function (sel) { return root.querySelector(sel); };
    var dressWrap = $('[data-fs-dress]');
    var heightWrap = $('[data-fs-height]');
    var measure = $('[data-fs-measure]');
    var regionWrap = $('[data-fs-region]');
    var result = $('[data-fs-result]');

    if (!hasUk && regionWrap) regionWrap.hidden = true;

    // --- dress chips -------------------------------------------------------
    function paintRegion() {
      if (!regionWrap) return;
      Array.prototype.forEach.call(regionWrap.querySelectorAll('[data-fs-region-btn]'), function (b) {
        b.classList.toggle('is-on', b.dataset.fsRegionBtn === state.region);
      });
    }

    function paintDress() {
      paintRegion();
      dressWrap.innerHTML = '';
      rows.forEach(function (r, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'fs-chip' + (state.dressIdx === i ? ' is-on' : '');
        b.textContent = state.region === 'uk' ? uk[i] : r.dress;
        b.setAttribute('aria-pressed', String(state.dressIdx === i));
        b.addEventListener('click', function () {
          state.dressIdx = i;
          state.mode = 'dress';
          paintDress();
          update();
          track('answer_size', { value: b.textContent, region: state.region });
        });
        dressWrap.appendChild(b);
      });
    }

    function paintHeight() {
      Array.prototype.forEach.call(heightWrap.querySelectorAll('[data-fs-h]'), function (b) {
        var on = state.height === b.dataset.fsH;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', String(on));
      });
    }

    // --- recommendation ----------------------------------------------------
    function toIn(v) {
      var n = parseFloat(v);
      if (!isFinite(n) || n <= 0) return null;
      return state.unit === 'cm' ? n / 2.54 : n;
    }

    function recommend() {
      var idx = null;
      var why = [];
      var measureBase = -1;

      if (state.mode === 'measure') {
        var w = toIn($('[data-fs-waist]').value);
        var h = toIn($('[data-fs-hips]').value);
        if (w == null && h == null) return null;

        // The published bands are integer-edged and the hip bands overlap, so
        // "first band whose upper bound clears the measurement" is the only
        // reading that has no gaps and no ambiguity.
        function band(v, key) {
          if (v == null) return -1;
          var i = rows.findIndex(function (r) { return r[key] && v <= r[key][1]; });
          return i < 0 ? rows.length - 1 : i;
        }
        var wi = band(w, 'waist');
        var hi = band(h, 'hips');

        var over = (w != null && w > rows[rows.length - 1].waist[1]) ||
                   (h != null && h > rows[rows.length - 1].hips[1]);

        idx = Math.max(wi, hi);
        // Never drift more than one band above what the waist says. Letting the
        // hip bump and the height bump stack sent a 26" waist to 2XL/3XL.
        var base = wi >= 0 ? wi : hi;
        measureBase = base;
        // Garments that must pass over the hips in one piece (bodysuits) opt out of the cap
        // via the chart's "hips first" setting, so the hip band always wins.
        if (root.dataset.hipsFirst !== 'true' && base >= 0 && idx > base + 1) idx = base + 1;
        if (hi > wi && wi >= 0 && root.dataset.noteHip) why.push(root.dataset.noteHip);
        if (over && root.dataset.noteOver) why.push(root.dataset.noteOver);
      } else {
        idx = state.dressIdx;
      }

      if (idx == null || idx < 0) return null;

      var floor = state.mode === 'measure' ? measureBase : idx;
      if (state.height === 'tall' && root.dataset.tallUp === 'true' && idx < rows.length - 1 && idx < floor + 1) {
        idx += 1;
        if (root.dataset.noteTall) why.push(root.dataset.noteTall);
      }
      return { idx: idx, size: rows[idx].size, why: why };
    }

    // --- apply -------------------------------------------------------------
    function applySize(size) {
      var n = 0;
      Array.prototype.forEach.call(document.querySelectorAll('fs-bundle-tiers select[data-opt]'), function (sel) {
        if (Array.prototype.some.call(sel.options, function (o) { return o.value === size; })) {
          sel.value = size;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          n += 1;
        }
      });
      Array.prototype.forEach.call(document.querySelectorAll('variant-picker select'), function (sel) {
        if (Array.prototype.some.call(sel.options, function (o) { return o.value === size; })) {
          sel.value = size;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          n += 1;
        }
      });
      Array.prototype.forEach.call(document.querySelectorAll('variant-picker input[type="radio"]'), function (i) {
        if (i.value === size && !i.checked) { i.click(); n += 1; }
      });
      return n;
    }

    // --- render ------------------------------------------------------------
    function update() {
      var rec = recommend();
      if (!rec) { result.hidden = true; return; }

      result.hidden = false;
      $('[data-fs-lead]').textContent = root.dataset.resultLead || 'Your match';
      $('[data-fs-size]').textContent = rec.size;
      var row = rows[rec.idx];
      var evidence = (root.dataset.evidence || '')
        .replace('{size}', row.size)
        .replace('{waist}', row.waistRaw || '')
        .replace('{hips}', row.hipsRaw || '');
      var ev = $('[data-fs-evidence]');
      if (ev) {
        ev.textContent = evidence;
        ev.hidden = !evidence;
      }
      $('[data-fs-why]').textContent = rec.why.join(' ');
      $('[data-fs-why]').hidden = rec.why.length === 0;

      var cta = $('[data-fs-apply]');
      cta.textContent = (root.dataset.applyLabel || 'Use {size}').replace('{size}', rec.size);
      cta.disabled = false;
      cta.onclick = function () {
        var n = applySize(rec.size);
        cta.textContent = n
          ? (root.dataset.applied || 'Done').replace('{size}', rec.size)
          : root.dataset.applyFail || 'Choose a size below';
        cta.disabled = true;
        track('apply', { size: rec.size, fields: n });
        setTimeout(function () {
          var d = root.closest('dialog');
          if (d && d.open) d.close();
        }, 700);
      };

      try {
        localStorage.setItem(
          STORE_KEY,
          JSON.stringify({ dressIdx: state.dressIdx, height: state.height, region: state.region, size: rec.size })
        );
      } catch (e) {}
      track('result', { size: rec.size, mode: state.mode });
    }

    // --- wiring ------------------------------------------------------------
    paintDress();
    paintHeight();

    heightWrap.addEventListener('click', function (e) {
      var b = e.target.closest('[data-fs-h]');
      if (!b) return;
      state.height = b.dataset.fsH; // single-select: re-tapping must not silently clear it
      paintHeight();
      update();
      track('answer_height', { value: b.textContent });
    });

    if (regionWrap) {
      regionWrap.addEventListener('click', function (e) {
        var b = e.target.closest('[data-fs-region-btn]');
        if (!b) return;
        state.region = b.dataset.fsRegionBtn;
        paintDress();
      });
    }

    var unitWrap = $('[data-fs-unit]');
    if (unitWrap) {
      unitWrap.addEventListener('click', function (e) {
        var b = e.target.closest('[data-fs-unit-btn]');
        if (!b) return;
        state.unit = b.dataset.fsUnitBtn;
        Array.prototype.forEach.call(unitWrap.children, function (c) {
          c.classList.toggle('is-on', c === b);
        });
        update();
      });
    }

    ['[data-fs-waist]', '[data-fs-hips]'].forEach(function (sel) {
      var el = $(sel);
      if (el) el.addEventListener('input', update);
    });
    var dressQ = dressWrap.previousElementSibling;
    if (dressQ && !dressQ.classList.contains('fs-finder__q')) dressQ = null;

    Array.prototype.forEach.call(root.querySelectorAll('[data-fs-mode]'), function (b) {
      b.addEventListener('click', function () {
        state.mode = b.dataset.fsMode;
        var m = state.mode === 'measure';
        measure.hidden = !m;
        dressWrap.hidden = m;
        if (dressQ) dressQ.hidden = m;
        root.querySelector('[data-fs-mode="measure"]').hidden = m;
        if (regionWrap) regionWrap.hidden = m || !hasUk;
        update();
        track('mode', { value: state.mode });
      });
    });

    if (state.dressIdx != null) update();
  }

  function initTabs(dialog) {
    var tabs = dialog.querySelectorAll('[data-fs-tab]');
    if (!tabs.length || dialog.dataset.fsTabs) return;
    dialog.dataset.fsTabs = '1';
    Array.prototype.forEach.call(tabs, function (tab) {
      tab.addEventListener('click', function () {
        Array.prototype.forEach.call(tabs, function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', String(on));
          var panel = dialog.querySelector('[data-fs-panel="' + t.dataset.fsTab + '"]');
          if (panel) panel.hidden = !on;
        });
        track('tab', { value: tab.dataset.fsTab });
      });
    });
  }

  // The size-chart block used to carry its own opener/closer inline. That script
  // was lost when the block was rewritten, which killed the X, the backdrop and
  // the second "Size guide" link. It lives here now so every entry point works.
  function bindDialogControls() {
    if (window.fsModalBound) return;
    window.fsModalBound = true;
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-fs-open]');
      if (opener) {
        var byId = document.getElementById(opener.getAttribute('data-fs-open'));
        var dlg = byId || document.querySelector('dialog.fs-modal');
        if (dlg && typeof dlg.showModal === 'function' && !dlg.open) {
          event.preventDefault();
          dlg.showModal();
          track('open', {});
        }
        return;
      }
      if (event.target.closest('[data-fs-close]')) {
        var open = event.target.closest('dialog');
        if (open) open.close();
        return;
      }
      // Clicking the backdrop reports the dialog itself as the target.
      if (event.target.matches('dialog.fs-modal')) event.target.close();
    });
  }

  function boot() {
    bindDialogControls();
    // Without an open event there is no denominator for the funnel.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-fs-open], [data-fs-open-guide]')) track('open', {});
    });
    Array.prototype.forEach.call(document.querySelectorAll('dialog.fs-modal'), initTabs);
    Array.prototype.forEach.call(document.querySelectorAll('.fs-finder'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();


/* ---- Size & fit picker -------------------------------------------------
   Builds the size pills from the chart's own <tr data-size> rows, so the
   measurements are never duplicated. Stays hidden until it finds rows.   */
(function () {
  function fitLine(tr) {
    var parts = [];
    var d = tr.getAttribute('data-dress');
    var w = tr.getAttribute('data-waist');
    var h = tr.getAttribute('data-hips');
    if (d) parts.push('dress ' + d);
    if (w) parts.push('waist ' + w);
    if (h) parts.push('hips ' + h);
    return parts.join(' \u00b7 ');
  }

  function build(box) {
    var wrap = box.closest('.fs-sizechart-wrap');
    if (!wrap) return;
    var rows = wrap.querySelectorAll('tr[data-size]');
    var grid = box.querySelector('[data-fs-sfg-grid]');
    var out = box.querySelector('[data-fs-sfg-value]');
    var between = box.querySelector('.fs-sfg__between');
    if (!rows.length || !grid) return;

    if (between) between.textContent = box.getAttribute('data-between') || '';
    grid.textContent = '';

    function select(btn, tr) {
      var pills = grid.querySelectorAll('.fs-sfg__pill');
      for (var i = 0; i < pills.length; i++) {
        pills[i].classList.remove('is-active');
        pills[i].setAttribute('aria-pressed', 'false');
      }
      btn.classList.add('is-active');
      btn.setAttribute('aria-pressed', 'true');
      if (out) out.textContent = fitLine(tr);
    }

    Array.prototype.forEach.call(rows, function (tr, i) {
      var label = tr.getAttribute('data-size');
      if (!label) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fs-sfg__pill';
      btn.textContent = label;
      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        select(btn, tr);
      });
      grid.appendChild(btn);
      if (i === 0) select(btn, tr);
    });

    box.hidden = false;
  }

  function init() {
    var boxes = document.querySelectorAll('[data-fs-sfg]');
    for (var i = 0; i < boxes.length; i++) build(boxes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
